/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerProfile.ts
 * PURPOSE: TanStack Query hooks màn Thông tin của tôi — Release v0 WP-5 / SC-013-MGR-PROFILE
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-29
 * COMPLIANCE: SDLC 6.3.1 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Manager — Hồ sơ cá nhân + tự chấm công VÀO/RA + đổi mật khẩu
 * - Impact: UX Critical — 1 page render theo role + có/không ca (profile.html / profile-bm.html)
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Custom Hooks (Business Logic)
 * - Pattern: TanStack Query v5 (useQuery, useMutation, useQueryClient)
 * - Dependencies: @tanstack/react-query, managerProfileApi
 *
 * @cross-references
 * - API Service: frontend/src/services/api/managerProfileApi.ts
 * - Page: frontend/src/pages/manager/ManagerProfilePage.tsx
 * - Sibling pattern: frontend/src/hooks/useManagerAttendance.ts
 * ===============================================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  managerProfileApi,
  type ChangePasswordPayload,
  type SelfClockPayload,
} from "../services/api/managerProfileApi";

export const MANAGER_PROFILE_QUERY_KEYS = {
  profile: ["manager", "profile", "me"] as const,
  status: ["manager", "profile", "attendance-status"] as const,
  clockConfig: ["manager", "profile", "clock-config"] as const,
  history: ["manager", "profile", "attendance-history"] as const,
  /**
   * 2026-09-04 — khoá RIÊNG cho truy vấn theo KHOẢNG NGÀY tuỳ ý (màn Lịch làm
   * việc, chế độ "Ngày"). KHÔNG dùng chung khoá `history` ở trên: khoá đó gắn
   * với cửa sổ cố định 7 ngày gần nhất, nếu dùng chung thì cache của một ngày
   * bất kỳ sẽ đè lên dữ liệu khối [6] "Gần đây" (và ngược lại).
   */
  historyRange: (start: string, end: string) =>
    ["manager", "profile", "attendance-history", start, end] as const,
  mySchedule: ["manager", "profile", "my-schedule"] as const,
  otSummary: (month: string) => ["manager", "profile", "ot-summary", month] as const,
  otSummaryRange: (start: string, end: string) =>
    ["manager", "profile", "ot-summary", start, end] as const,
};

/** Khối [2] Hồ sơ (read) */
export function useManagerSelfProfile() {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.profile,
    queryFn: () => managerProfileApi.getProfile(),
    staleTime: 60_000,
    retry: 2,
  });
}

/** Khối [4] trạng thái chấm công hôm nay (ca + đã vào/ra + duyệt + GPS) */
export function useSelfAttendanceStatus() {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.status,
    queryFn: () => managerProfileApi.getAttendanceStatus(),
    staleTime: 15_000,
    retry: 2,
  });
}

/** Popup [P] — phương thức chấm công được bật ở chi nhánh (SC-004) */
export function useClockConfig() {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.clockConfig,
    queryFn: () => managerProfileApi.getClockConfig(),
    staleTime: 60_000,
    retry: 2,
  });
}

/** Khối [6] Gần đây — 7 ngày gần nhất (bao gồm hôm nay, FE lọc khi render) */
export function useSelfAttendanceHistory() {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.history,
    queryFn: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const iso = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return managerProfileApi.getHistory(iso(start), iso(end));
    },
    staleTime: 30_000,
    retry: 2,
  });
}

/**
 * Bản ghi chấm công THẬT trong một khoảng ngày tuỳ ý — dùng cho màn Lịch làm
 * việc chế độ "Ngày" (khối "Chấm công thực tế"). Cùng endpoint
 * `GET /api/v2/attendance/history` mà khối [6] đang dùng, chỉ khác tham số
 * khoảng ngày, nên không phát sinh hợp đồng API mới.
 *
 * `enabled` để trang chỉ gọi khi thật sự đang ở chế độ Ngày (Tuần/Tháng không
 * cần khối này).
 */
export function useSelfAttendanceHistoryRange(
  startDate: string,
  endDate: string,
  enabled = true,
) {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.historyRange(startDate, endDate),
    queryFn: () => managerProfileApi.getHistory(startDate, endDate),
    enabled: enabled && Boolean(startDate) && Boolean(endDate),
    staleTime: 30_000,
    retry: 1,
  });
}

/** Lịch ca tuần này — nhận diện ngày "— (nghỉ theo lịch)" ở khối [6] */
export function useMySchedule() {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.mySchedule,
    queryFn: () => managerProfileApi.getMySchedule(),
    staleTime: 60_000,
    retry: 2,
  });
}

/**
 * BS-301 Route 2 §1 (2026-08-17, emp-06 "lịch sử+OT") — thẻ tổng hợp tăng ca
 * tháng hiện tại (ngày thường/cuối tuần/lễ/ca đêm), trần OT còn lại, số lần
 * đi muộn/về sớm/vi phạm nghỉ giữa ca. `month` defaults to the current
 * YYYY-MM so AttendanceHistory.tsx doesn't need to compute it itself.
 */
export function useSelfOtSummary(month?: string) {
  const resolvedMonth = month ?? new Date().toISOString().slice(0, 7);
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.otSummary(resolvedMonth),
    queryFn: () => managerProfileApi.getOtSummary(resolvedMonth),
    staleTime: 30_000,
    retry: 2,
  });
}

/**
 * Tổng hợp OT/vi phạm cho một KHOẢNG NGÀY tuỳ ý — dùng ở màn Lịch sử chấm công
 * để thống kê nói về đúng khoảng đang xem. Xem `getOtSummaryRange` (lý do +
 * bằng chứng curl) trong managerProfileApi.ts.
 */
export function useSelfOtSummaryRange(startDate: string, endDate: string, enabled = true) {
  return useQuery({
    queryKey: MANAGER_PROFILE_QUERY_KEYS.otSummaryRange(startDate, endDate),
    queryFn: () => managerProfileApi.getOtSummaryRange(startDate, endDate),
    enabled: enabled && Boolean(startDate) && Boolean(endDate),
    staleTime: 30_000,
    retry: 1,
  });
}

/** Invalidate status + history sau khi chấm công thành công (CLOCK-DONE) */
function useInvalidateSelfClock() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: MANAGER_PROFILE_QUERY_KEYS.status }),
      queryClient.invalidateQueries({ queryKey: MANAGER_PROFILE_QUERY_KEYS.history }),
    ]);
}

/** Xác nhận VÀO (modal [C1]/[C2]) → toast "Đã chấm công" */
export function useSelfCheckIn() {
  const invalidate = useInvalidateSelfClock();
  return useMutation({
    mutationFn: (payload: SelfClockPayload) => managerProfileApi.checkIn(payload),
    onSuccess: invalidate,
  });
}

/** Xác nhận RA (modal [C1]/[C2]) → toast "Đã chấm công" + ẩn nút [5] */
export function useSelfCheckOut() {
  const invalidate = useInvalidateSelfClock();
  return useMutation({
    mutationFn: (payload: SelfClockPayload) => managerProfileApi.checkOut(payload),
    onSuccess: invalidate,
  });
}

/** Modal [M] Đổi mật khẩu (self, cần MK hiện tại) → toast "Đã đổi mật khẩu" */
export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) => managerProfileApi.changePassword(payload),
  });
}
